"use client";
import { useFormik } from "formik";
import { useQueryParams } from "../../../../utils/QueryParamsProvider/QueryParamsProvider.js";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { getFiltersInitialWithUrlParams, getFiltersInitial, validateForm } from "../../../../utils/warehouses/findAWarehouse.lib.js";
import { LOCATION_SEARCH_PARAM_KEY, LIST_BOX_ARIA_LABEL_KEY, DRAWER_ARIA_LABEL_KEY, WAREHOUSE_FILTERS_FIELDSET_KEY, HIDE_WAREHOUSE_FILTERS_BTN_LABEL_KEY, SHOW_WAREHOUSE_FILTERS_BTN_LABEL_KEY, SEARCH_FIELD_LABEL_KEY } from "../../../../utils/warehouses/warehouseDrawer.constants.js";
import { useState, useMemo, useCallback } from "react";
import { useTracking } from "react-tracking";
import { removeDuplicateAddresses, getOptionLabel } from "../../utils/warehouse.lib.js";
const useWarehouseSearch = ({
  moduleSettings,
  onSearch,
  defaultFiltersVisible,
  getAutosuggest,
  drawerType,
  syncWithUrl = false,
  analyticsEventTemplate
}) => {
  const { trackEvent } = useTracking();
  const { queryParams } = useQueryParams();
  const [isAutocompleteDropdownOpen, setIsAutocompleteDropdownOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(
    defaultFiltersVisible ?? false
  );
  const [options, setOptions] = useState([]);
  const [locationSearchValue, setLocationSearchValue] = useState(null);
  const locationSearchParam = queryParams.get(LOCATION_SEARCH_PARAM_KEY);
  const msStore = useMemo(
    () => new ModuleSettingsStore({
      composable_window: moduleSettings.composable_window,
      key_value: moduleSettings.key_value
    }),
    [moduleSettings]
  );
  const searchTextInitialValue = syncWithUrl && locationSearchParam ? locationSearchParam : "";
  const initialValues = useMemo(
    () => ({
      filters: syncWithUrl ? getFiltersInitialWithUrlParams(moduleSettings, queryParams) : getFiltersInitial(moduleSettings),
      searchText: searchTextInitialValue
    }),
    [moduleSettings, searchTextInitialValue, syncWithUrl, queryParams]
  );
  const formik = useFormik({
    enableReinitialize: syncWithUrl,
    initialValues,
    onSubmit: (values) => {
      const defaultEvent = {
        identifier: "whSearchTerm",
        location: `${drawerType === "FMW" ? "find a warehouse" : "check nearby warehouses"} flyout`,
        trackType: "search"
      };
      const eventToTrack = {
        ...defaultEvent,
        ...analyticsEventTemplate,
        keyword: values.searchText,
        trackType: (analyticsEventTemplate == null ? void 0 : analyticsEventTemplate.trackType) ?? "search"
      };
      trackEvent(eventToTrack);
      onSearch(values);
      setLocationSearchValue(values.searchText);
    },
    validate: (values) => validateForm(moduleSettings, values)
  });
  const fetchAndSetAutosuggestions = useCallback(
    async (q) => {
      var _a;
      try {
        const results = await (getAutosuggest == null ? void 0 : getAutosuggest(q));
        const suggestions = ((_a = results == null ? void 0 : results.resourceSets[0].resources[0].value) == null ? void 0 : _a.filter((obj) => {
          var _a2;
          return !!((_a2 = obj == null ? void 0 : obj.address) == null ? void 0 : _a2.formattedAddress);
        }).map((obj) => ({
          address: obj.address.formattedAddress,
          locality: obj.address.locality,
          postalCode: obj.address.postalCode
        }))) || [];
        setOptions(removeDuplicateAddresses(suggestions));
      } catch {
        setOptions([]);
      }
    },
    [getAutosuggest]
  );
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      formik.handleSubmit();
    },
    [formik]
  );
  const onInputChange = useCallback(
    (event, newValue, reason) => {
      if (!event && !newValue) {
        return null;
      } else if (event && reason === "reset") {
        !isAutocompleteDropdownOpen && handleSubmit(event);
      } else {
        if ((event == null ? void 0 : event.code) !== "Enter" && (event == null ? void 0 : event.code) !== "NumpadEnter") {
          formik.setFieldValue("searchText", newValue);
          if (newValue.length > 0) {
            fetchAndSetAutosuggestions(newValue);
          } else {
            setOptions([]);
          }
        }
      }
    },
    [
      fetchAndSetAutosuggestions,
      formik,
      handleSubmit,
      isAutocompleteDropdownOpen
    ]
  );
  const selectFirstOption = useCallback(() => {
    if (options.length > 0) {
      formik.setFieldValue("searchText", options[0].address);
    }
  }, [options, formik]);
  const labels = useMemo(
    () => ({
      searchFieldLabel: msStore.getKeyValueItem(SEARCH_FIELD_LABEL_KEY),
      filters: {
        showWarehouseFiltersBtnText: msStore.getKeyValueItem(
          SHOW_WAREHOUSE_FILTERS_BTN_LABEL_KEY
        ),
        hideWarehouseFiltersBtnText: msStore.getKeyValueItem(
          HIDE_WAREHOUSE_FILTERS_BTN_LABEL_KEY
        ),
        warehouseFiltersLegend: msStore.getKeyValueItem(
          WAREHOUSE_FILTERS_FIELDSET_KEY
        )
      },
      aria: {
        panelAriaLabel: msStore.getKeyValueItem(DRAWER_ARIA_LABEL_KEY),
        listBoxAriaLabel: msStore.getKeyValueItem(
          LIST_BOX_ARIA_LABEL_KEY
        )
      }
    }),
    [msStore]
  );
  return {
    formik,
    options,
    labels,
    isFiltersOpen,
    isAutocompleteDropdownOpen,
    locationSearchParam: locationSearchValue || locationSearchParam,
    actions: {
      setIsFiltersOpen,
      setIsAutocompleteDropdownOpen,
      setOptions,
      selectFirstOption,
      getOptionLabel
    },
    handlers: {
      handleSubmit,
      onInputChange,
      fetchSuggestions: fetchAndSetAutosuggestions
    }
  };
};
export {
  useWarehouseSearch
};
